import { batch } from 'react-redux';
import { handleCashbackAmount } from 'src/cashback/business-logic/handle-cashback-amount';
import { showCashbackRequestDialog } from 'src/cashback/cashback-request';
import { restorePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import { TenderType } from 'src/common/enumerations/tender-type';
import { TenderChangeAvailability } from 'src/common/helpers/error-code';
import { EpsData } from 'src/devices/payment/dtos/eps-data';
import { isCartFinalized } from 'src/home-page/business-logic/complete-transaction';
import { TenderingModeHomePage } from 'src/home-page/components/home-page-tendering';
import { EntryMethod } from '../../common/enumerations/entry-method';
import { ITenderAdd, PaymentUsage, RefundUsage } from '../../common/interfaces/dtos/IPreTenderAdd';
import { ITender } from '../../common/interfaces/dtos/ITender';
import { store } from '../../common/redux/configure-store';
import { TenderMenuActions } from '../../common/redux/reducers/tender-menu-reducer';
import { getPaymentOptions } from '../../home-page/business-logic/get-payment-options';
import { makeTenderPayment } from '../../home-page/business-logic/make-tender-payment';
import { preMakeTenderPayment } from '../../home-page/business-logic/pre-make-tender-payment';
import { resetCardData } from './../../devices/payment/get-card-details';
import { BuildLineTenderProps, buildTenderAddDTO } from './build-line-tender';
import { addEFTTender, showTenderingAndGetCardDetails } from './handle-eft-tendering';
import { EwicError } from './process-ewic-tender';
import { showTenderingError } from './show-tendering-error';
import { setDefaultTenderState } from './tendering-actions';

export interface AddTenderProps {
  tender: ITender;
  amount: number;
  amountDue: number;
  cashbackAmount?: number;
  lineItemEntryMethod: EntryMethod;
  usage?: PaymentUsage | RefundUsage;
}

export const submitPayment = async (
  amount: number,
  lineItemEntryMethod: EntryMethod,
  tender: ITender,
): Promise<void> => {
  const amountDue = store.getState().itemList.amountDue;
  const tenderProps: AddTenderProps = {
    tender,
    amount,
    amountDue,
    lineItemEntryMethod,
  };

  if (tender.isProcessedByEps) {
    if (tender.isAuthorizeWithoutIdentification && tender.initiateCashbackRequest === 'YesR10') {
      await showCashbackRequestDialog()
        .then(() => handleCashbackAmount(amountDue))
        .then(cashbackAmount => (tenderProps.cashbackAmount = cashbackAmount))
        .catch(() => {});
    }
    return showTenderingAndGetCardDetails(tenderProps)
      .then(() => addEFTTender(tenderProps))
      .then(() => closeTenderingAndSetPartialPaymentTab(true))
      .catch(err => {
        if (err.code === TenderChangeAvailability) {
          store.dispatch(TenderMenuActions.setSelectedTender(tender));
          restorePage();
        } else {
          restorePage(TenderingModeHomePage);
        }
        if (err.code !== EwicError) {
          setDefaultTenderState();
        }
        return showTenderingError(err);
      });
  } else {
    return addTender(tenderProps)
      .then(() => closeTenderingAndSetPartialPaymentTab(false, tender.type))
      .catch(showTenderingError);
  }
};

export const addTender = async (props: AddTenderProps): Promise<void> => {
  const { tender, amount, amountDue, lineItemEntryMethod, usage } = props;

  const buildLineProps: BuildLineTenderProps = {
    tender,
    amount,
    lineItemEntryMethod,
    usage,
  };

  if (amountDue !== amount && store.getState().tenderMenu.selectedTenderGroup?.name !== 'Checks') {
    buildLineProps.amountEntryMethod = EntryMethod.Manual;
  }
  const lineTender = buildTenderAddDTO(buildLineProps);
  await preMakeTenderPayment(lineTender);
  await makeTenderPayment(lineTender);
};

export const makePaymentAndSetDefaultTender = async (tenderAdd: ITenderAdd): Promise<void> => {
  await makeTenderPayment(tenderAdd);
  closeTenderingAndSetPartialPaymentTab(
    tenderAdd.tender?.processedByEps,
    tenderAdd.tender?.tenderType,
  );
};

const closeTenderingAndSetPartialPaymentTab = async (
  processedByEps?: boolean,
  tenderType?: TenderType,
): Promise<void> => {
  const cartStatus = store.getState().transaction.cartStatus;
  if (!isCartFinalized(cartStatus)) {
    await getPaymentOptions();
    // Need reset EpsData because we return to TenderView Component (Partial Payment, POS return approved only part of the amount)
    if (processedByEps) {
      resetCardData();
    }

    // restorePage to remove any Tendering page except voucher as voucher has a specific voucher page
    if (tenderType !== TenderType.Voucher) {
      restorePage(TenderingModeHomePage);
    }
    setDefaultTenderState();
  }
};

export const findMatchingTenderInStore = (epsData: EpsData): ITender | undefined => {
  const cardCode = epsData.card.cardCode || '';
  const validTenders = store.getState().tenderMenu.sellableTenders.rawTenders;

  const matchingTender = validTenders.find(tender => tender.tenderReferences?.includes(cardCode));

  return matchingTender;
};

export const setSelectedTenderAndGroup = (tender: ITender): void => {
  const tenderGroups = store.getState().tenderMenu.sellableTenders.tenderGroups;
  const newTenderGroup = tenderGroups.find(tenderGroup => tenderGroup.tenders.includes(tender));
  batch(() => {
    store.dispatch(TenderMenuActions.setSelectedTender(tender));
    store.dispatch(TenderMenuActions.setSelectedTenderGroup(newTenderGroup?.name));
  });
};
